import { VStack, Heading, Image, Text, Box } from '@chakra-ui/react';
import { IBook } from '@partiu/api/types';
import { PiHeartFill } from 'react-icons/pi';
import { SlEarphones } from 'react-icons/sl';
import { badges } from '../../pages/Profile/components/Plans';
import { FaRegStar } from 'react-icons/fa';
import { useOpenBook } from '../../contexts/BookContext';

interface BookProps {
  book: IBook;
  receivedBook?: boolean;
  deliveryCode?: string;
  bestSellerName?: string;
  showEditorialCode?: boolean;
  monthReference?: number;
  yearReference?: number;
}

const monthNames = [
  'Janeiro',
  'Fevereiro',
  'Marco',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export function Book({
  book,
  bestSellerName,
  receivedBook,
  deliveryCode,
  showEditorialCode = true,
  monthReference,
  yearReference,
}: BookProps) {
  const { openBook } = useOpenBook();

  const cover = book.coverOptimized || book.cover;
  const issueNumber = (book as IBook & { issueNumber?: string }).issueNumber;
  const magazineMonth = (book as IBook & { referenceMonth?: number }).referenceMonth;
  const magazineYear = (book as IBook & { referenceYear?: number }).referenceYear;

  const editionMonth = book.type === 'magazine' ? monthReference || magazineMonth : undefined;
  const editionYear = book.type === 'magazine' ? yearReference || magazineYear : undefined;

  const onChangeFavorite = (favorite: boolean) => {
    book.isFavorite = favorite;
  };

  return (
    <VStack
      position={'relative'}
      spacing={0}
      cursor={'pointer'}
      onClick={() => openBook(book, receivedBook, onChangeFavorite, deliveryCode)}
      align={'flex-start'}
      width={'100%'}
    >
      {book.type === 'audiobook' && (
        <VStack position={'absolute'} top="5px" left="5px">
          <SlEarphones size={25} color={'white'} filter="drop-shadow(0px 0px 3px rgba(0,0,0,1))" />
        </VStack>
      )}
      {book.isBestSeller ||
        (bestSellerName && (
          <Box
            display={'flex'}
            position={'absolute'}
            top={2}
            left={2}
            flexDirection={'row'}
            alignItems={'center'}
            gap={1}
            padding="3px"
            paddingX="10px"
            borderRadius="6px"
            backgroundColor={'#000000B3'}
          >
            <FaRegStar color="white" size={15} />
            <Text fontSize={12} color="white">
              {bestSellerName || 'Best Seller'}
            </Text>
          </Box>
        ))}
      <VStack position={'absolute'} top="5px" right="5px">
        {badges.map(
          (badge) =>
            book.levels.includes(badge.id) && (
              <Image
                key={badge.id}
                filter="drop-shadow(0px 0px 3px rgba(0,0,0,1))"
                src={`/${badge?.image}`}
                height={'24px'}
              />
            )
        )}
      </VStack>
      {book.isFavorite && (
        <VStack position={'absolute'} top="5px" right="30px">
          <PiHeartFill fontSize={'20px'} color="red" />
        </VStack>
      )}
      <Image
        boxShadow={'rgba(100, 100, 111, 0.2) 0px 5px 8px 0px'}
        borderRadius={'5px'}
        aspectRatio={0.7}
        border={'1px solid #ededed'}
        width={'100%'}
        objectFit={{ base: 'contain', md: 'cover' }}
        backgroundColor="white"
        src={cover}
      />
      <Heading
        paddingTop="8px"
        width={'100%'}
        whiteSpace={'nowrap'}
        overflowX={'clip'}
        textOverflow={'ellipsis'}
        fontSize={'12px'}
      >
        {book.title}
      </Heading>
      {book.type !== 'magazine' && (
        <Text
          color="#787878"
          fontSize={'12px'}
          width={'100%'}
          whiteSpace={'nowrap'}
          overflowX={'clip'}
          textOverflow={'ellipsis'}
        >
          {book.author}
        </Text>
      )}
      {showEditorialCode && book?.isbn && (
        <Text
          color="#787878"
          fontSize={'12px'}
          width={'100%'}
          whiteSpace={'nowrap'}
          overflowX={'clip'}
          textOverflow={'ellipsis'}
        >
          {book.type === 'magazine' ? 'ISSN' : 'ISBN'}: {book.isbn}
        </Text>
      )}
      {book.type === 'magazine' && issueNumber && (
        <Text
          color="#787878"
          fontSize={'12px'}
          width={'100%'}
          whiteSpace={'nowrap'}
          overflowX={'clip'}
          textOverflow={'ellipsis'}
        >
          Edição nº {issueNumber}
        </Text>
      )}
      {receivedBook && editionMonth !== undefined && editionYear && (
        <Text
          color="#787878"
          fontSize={'12px'}
          width={'100%'}
          whiteSpace={'nowrap'}
          overflowX={'clip'}
          textOverflow={'ellipsis'}
        >
          {monthNames[Math.max(0, Math.min(11, editionMonth))]} {editionYear}
        </Text>
      )}
    </VStack>
  );
}
